import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Collapse, Form, ListGroup, Spinner, Table } from 'react-bootstrap';
import { getDeliveries } from '../api';
import ProductDetailsModal from './ProductDetailsModal';
import OrderDetailsModal from './OrderDetailsModal';
import AssignDeliveryModal from './AssignDeliveryModal';
import DeliveryTicketPanel from './DeliveryTicketPanel';
import InfoTip from './InfoTip';
import { deliveryStatusReason } from './statusHelp';

// fix_issues.md follow-up - replaces the old Upcoming/Past/All tabs. "This year"/"Last year" are
// fiscal years (1 April - 31 March), matching common/models.js's shape and routes/ownerCrud.js's
// resolveDeliveryDateRange, which is the source of truth these labels describe.
const RANGE_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' }
];

function formatAddress(addr) {
  if (!addr?.line1) return null;
  return [addr.line1, addr.line2, addr.area, addr.city, addr.pincode].filter(Boolean).join(', ');
}

// Owner Bookings & Cancellation v2 doc, Story 14 - one list component shared by the owner/admin/
// delivery-person "Deliveries" tabs (GET .../bookings/deliveries scopes the same call differently
// server-side per role - see routes/ownerCrud.js). 20 records per page by default; scrolling near
// the bottom fetches the next page (infinite scroll, not a "load more" button). `showSearch` is
// only meaningful for owner/admin (the backend ignores `search` for other roles anyway, since
// their lists are already scoped to "mine") - also gates the Story 17 "Reassign" action below,
// since only owner/admin can assign delivery at all.
export default function DeliveryList({ showSearch = false }) {
  const [range, setRange] = useState('today');
  const [filterOpen, setFilterOpen] = useState(false); // fix_issues.md follow-up - collapsed by default, same on mobile and desktop
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productItemId, setProductItemId] = useState(null);
  const [detailsBooking, setDetailsBooking] = useState(null); // fix_issues.md #4
  const [reassignBooking, setReassignBooking] = useState(null); // Story 17
  const loadingRef = useRef(false);

  const loadPage = useCallback(async (targetPage, replace) => {
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await getDeliveries({ range, search, page: targetPage });
      setItems((prev) => (replace ? res.data : [...prev, ...res.data]));
      setPage(res.pagination.page);
      setHasMore(res.pagination.hasMore);
    } catch {
      setError('Could not load deliveries.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [range, search]);

  // Reset and reload from page 1 whenever the selected range or the applied search term changes.
  useEffect(() => {
    loadPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, search]);

  useEffect(() => {
    const onScroll = () => {
      if (loadingRef.current || !hasMore) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 300;
      if (nearBottom) loadPage(page + 1, false);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasMore, page, loadPage]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  // fix_issues.md follow-up - the website owner (admin) sees the rental owner's own details on
  // every booking (routes/ownerCrud.js's attachOwnerDetails); an owner viewing their own listings
  // never gets this field back, so the column only appears when it's actually meaningful.
  const showOwnerColumn = items.some((b) => b.ownerName);
  const activeRangeLabel = RANGE_OPTIONS.find((o) => o.key === range)?.label;

  return (
    <div className="d-flex flex-column flex-md-row gap-3">
      {/* fix_issues.md follow-up - a collapsible filter, not tabs: sits to the left of the list on
          a wide screen (flex-row), stacks above it and stays collapsible to save vertical space on
          a narrow one (flex-column) - same toggle behavior either way. */}
      <div style={{ minWidth: 200, maxWidth: 240 }}>
        <Button
          variant="outline-secondary" size="sm"
          className="w-100 d-flex justify-content-between align-items-center"
          onClick={() => setFilterOpen((o) => !o)}
          aria-expanded={filterOpen}
        >
          <span>Filter: {activeRangeLabel}</span>
          <span aria-hidden="true">{filterOpen ? '▲' : '▼'}</span>
        </Button>
        <Collapse in={filterOpen}>
          <div>
            <ListGroup className="mt-2">
              {RANGE_OPTIONS.map((opt) => (
                <ListGroup.Item
                  key={opt.key} action active={opt.key === range}
                  onClick={() => { setRange(opt.key); setFilterOpen(false); }}
                >
                  {opt.label}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </Collapse>
      </div>

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        {showSearch && (
          <Form onSubmit={handleSearchSubmit} className="mb-3 d-flex gap-2" style={{ maxWidth: 420 }}>
            <Form.Control
              size="sm" placeholder="Search by customer or item name"
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button size="sm" type="submit">Search</Button>
          </Form>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {items.length === 0 && !loading ? (
          <Alert variant="light">No deliveries here.</Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                {/* fix_issues.md #4 - order id is always visible (mobile included); dates/scheduled
                    move behind the Details popup below the sm breakpoint instead of forcing
                    horizontal scrolling on a small screen. */}
                <th>Order ID</th>
                <th>Customer</th>
                {showOwnerColumn && <th className="d-none d-md-table-cell">Owner</th>}
                <th className="d-none d-md-table-cell">Dates</th>
                <th>Delivery status</th>
                {/* fix_issues.md #3 - the assign/reassign action now lives in this same column
                    instead of its own trailing one. */}
                <th>Assigned to</th>
                <th className="d-none d-md-table-cell">Scheduled</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Story 17 - a booking that needs reassignment (a delivery person declined/is
                  unavailable) sorts to the top, same "surface what needs attention first" pattern
                  as Story 13b's return-due sort. */}
              {[...items].sort((a, b) => Number(!!b.deliveryNeedsReassignment) - Number(!!a.deliveryNeedsReassignment)).map((booking) => (
                <tr key={booking._id} className={booking.deliveryNeedsReassignment ? 'table-warning' : undefined}>
                  <td>
                    <Button size="sm" variant="link" className="p-0" onClick={() => setDetailsBooking(booking)}>
                      #{booking._id.slice(-6).toUpperCase()}
                    </Button>
                  </td>
                  <td>
                    {booking.customerName}
                    <br />
                    <small className="text-muted">{booking.email} &middot; {booking.phoneNumber}</small>
                    {/* delivery_ticket_pickup_drop_distance.md - the full route (pick up + drop
                        off, each with a map link, and the driving distance), directly in the row
                        so a delivery person / owner / admin can act on it without opening Details.
                        Falls back to just the customer address for pre-ticket bookings. */}
                    {booking.deliveryTicket ? (
                      <DeliveryTicketPanel ticket={booking.deliveryTicket} className="mt-1" />
                    ) : formatAddress(booking.deliveryAddress) && (
                      <>
                        <br />
                        <small className="text-muted">📍 {formatAddress(booking.deliveryAddress)}</small>
                      </>
                    )}
                  </td>
                  {showOwnerColumn && (
                    <td className="d-none d-md-table-cell">
                      {booking.ownerName ? (
                        <>
                          {booking.ownerName}
                          {booking.ownerPhone && <><br /><small className="text-muted">{booking.ownerPhone}</small></>}
                        </>
                      ) : <span className="text-muted">—</span>}
                    </td>
                  )}
                  <td className="d-none d-md-table-cell">{new Date(booking.startDate).toLocaleDateString()} &ndash; {new Date(booking.endDate).toLocaleDateString()}</td>
                  <td>
                    <InfoTip text={deliveryStatusReason(booking)}>
                      <Badge bg="secondary">{booking.deliveryStatus.replace(/_/g, ' ')}</Badge>
                    </InfoTip>
                  </td>
                  <td>
                    {booking.deliveryNeedsReassignment ? (
                      <InfoTip text="The assigned delivery person declined or is unavailable — this needs a replacement before the item can go out.">
                        <Badge bg="warning" text="dark">Needs reassignment</Badge>
                      </InfoTip>
                    ) : booking.deliveryAssignment?.assignedTo?.name ? (
                      <>
                        {booking.deliveryAssignment.assignedTo.name}
                        {booking.deliveryAssignment.autoAssigned && <Badge bg="info" text="dark" className="ms-1">Auto-assigned</Badge>}
                      </>
                    ) : (
                      <span className="text-muted">Unassigned</span>
                    )}
                    {showSearch && (booking.deliveryNeedsReassignment || !booking.deliveryAssignment?.assignedTo) && (
                      <Button size="sm" variant="outline-warning" className="ms-2" onClick={() => setReassignBooking(booking)}>
                        {booking.deliveryNeedsReassignment ? 'Reassign' : 'Assign'}
                      </Button>
                    )}
                  </td>
                  <td className="d-none d-md-table-cell">{booking.deliveryAssignment?.scheduledAt ? new Date(booking.deliveryAssignment.scheduledAt).toLocaleString() : '—'}</td>
                  <td>
                    <Button size="sm" variant="outline-secondary" onClick={() => setDetailsBooking(booking)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {loading && <Spinner animation="border" size="sm" role="status" />}
      </div>

      <ProductDetailsModal itemId={productItemId} show={!!productItemId} onHide={() => setProductItemId(null)} />

      {/* fix_issues3.md - one shared order popup (was an inline Modal here, fix_issues.md #4), so a
          small screen (where the Dates/Scheduled/Owner columns above are hidden) never loses
          access to them, and every "open the order" surface looks the same. */}
      <OrderDetailsModal
        booking={detailsBooking}
        show={!!detailsBooking}
        onHide={() => setDetailsBooking(null)}
        onViewProduct={detailsBooking ? () => setProductItemId(detailsBooking.itemId) : undefined}
      />

      {showSearch && (
        <AssignDeliveryModal
          booking={reassignBooking}
          show={!!reassignBooking}
          onHide={() => setReassignBooking(null)}
          onAssigned={() => {
            setReassignBooking(null);
            loadPage(1, true);
          }}
        />
      )}
    </div>
  );
}
